import { Modal } from '@design-system/components/modal/modal';
import { Input } from '@design-system/components/input/input';
import { Typography } from '@design-system/components/typography/typography';
import auth from '@client/components/auth';
import InfluencerSelect from '@client/components/InfluencerSelect';
import { PortalUser, PortalUserInput } from '../../model/PortalUserList.types';
import { usePortalUserFormViewModel } from '../../view-model/PortalUserForm.viewModel';
import * as styles from './PortalUserFormModal.css';

type Props = {
  user: PortalUser | null;
  onClose: () => void;
  save: (input: PortalUserInput) => Promise<void>;
};

export const PortalUserFormModal = ({ user, onClose, save }: Props) => {
  const vm = usePortalUserFormViewModel({
    user,
    isAdministrator: auth.isAdministrator(),
    isModerator: auth.isModerator(),
    save,
    onClose,
  });

  return (
    <Modal
      isOpen
      onClose={vm.lazyDispatch('Close')}
      title={vm.title}
      testId="portal-user-form"
      modalContentClassName={styles.content}
      primaryCta={{
        caption: vm.isSaving ? 'Saving…' : 'Save',
        onClick: vm.lazyDispatch('Submit'),
        state: vm.isSaving ? 'disabled' : undefined,
      }}
      secondaryCta={{
        caption: 'Cancel',
        onClick: vm.lazyDispatch('Close'),
        state: vm.isSaving ? 'disabled' : undefined,
      }}
    >
      <div className={styles.row}>
        <Input
          label="First name"
          name="firstName"
          placeholder="First name"
          value={vm.draft.firstName}
          onChange={(e) => vm.dispatch('SetFirstName', e.target.value)}
          hideError
        />
        <Input
          label="Last name"
          name="lastName"
          placeholder="Last name"
          value={vm.draft.lastName}
          onChange={(e) => vm.dispatch('SetLastName', e.target.value)}
          hideError
        />
      </div>
      <Input
        label="E-mail *"
        name="email"
        placeholder="Email"
        value={vm.draft.email}
        onChange={(e) => vm.dispatch('SetEmail', e.target.value)}
        hideError
      />
      <div className={styles.row}>
        <label className={styles.field}>
          <Typography variant="span" size="small" fontWeight="medium">
            Role
          </Typography>
          <select
            className={styles.select}
            value={vm.draft.role}
            onChange={(e) => {
              const option = vm.roleOptions.find(
                ({ value }) => String(value) === e.target.value,
              );
              if (option) vm.dispatch('SetRole', option.value);
            }}
          >
            {vm.roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {vm.showPartner ? (
          <div className={styles.field}>
            <Typography variant="span" size="small" fontWeight="medium">
              Related Partner *
            </Typography>
            <InfluencerSelect
              influencerId={vm.draft.influencerId}
              onChange={(option: { value: number } | null) =>
                vm.dispatch('SetInfluencerId', {
                  influencerId: option?.value ?? null,
                })
              }
            />
          </div>
        ) : null}
      </div>
      {vm.errors.length > 0 ? (
        <div className={styles.errors} role="alert">
          {vm.errors.map((message) => (
            <Typography
              key={message}
              variant="span"
              size="small"
              color="ERROR-100"
            >
              {message}
            </Typography>
          ))}
        </div>
      ) : null}
    </Modal>
  );
};
